import { voyagerTrack } from '@shared/voyager';
import { PUBLIC_WEBSITE_URL } from './content';

type TrackingValue = boolean | number | string | undefined | { [key: string]: TrackingValue };
type TrackingParams = Record<string, TrackingValue>;

export const PLATFORM_WEBSITE_TRACKING_DEBUG_EVENT = 'platform-website:tracking-debug';

export const PLATFORM_WEBSITE_TRACKING_EVENTS = {
  SEARCH: 'pc_big_search',
  CHAT: 'pc_im_icon_click',
  NAVIGATION_SHOW: 'maimai.all.all.show',
  COMPANY_GUIDE: 'bprofile_nav_guide',
  REFERRAL_SHOW: 'maimai.market_data_report_icon.show',
  REFERRAL_CLICK: 'maimai.market_data_report_icon.click',
} as const;

type PlatformWebsiteTrackingEvent =
  (typeof PLATFORM_WEBSITE_TRACKING_EVENTS)[keyof typeof PLATFORM_WEBSITE_TRACKING_EVENTS];

const SAFE_DEBUG_PARAM_KEYS = new Set([
  'page_url',
  'fr',
  'badge',
  'enter_place',
  'target_type',
  'top_navi_name_and_badge',
]);

function getSafeDebugParams(params: TrackingParams): TrackingParams {
  return Object.fromEntries(Object.entries(params).filter(([key]) => SAFE_DEBUG_PARAM_KEYS.has(key)));
}

function track(eventName: PlatformWebsiteTrackingEvent, params: TrackingParams = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  const trackingParams = {
    ...params,
    page_url: PUBLIC_WEBSITE_URL,
  };

  if (process.env.NODE_ENV !== 'production') {
    try {
      window.dispatchEvent(
        new CustomEvent(PLATFORM_WEBSITE_TRACKING_DEBUG_EVENT, {
          detail: {
            eventName,
            params: getSafeDebugParams(trackingParams),
          },
        })
      );
    } catch {
      // Local diagnostics must never make the public website unreadable.
    }
    return;
  }

  try {
    Promise.resolve(voyagerTrack(eventName, trackingParams)).catch(() => undefined);
  } catch {
    // Analytics must never make the public website unreadable.
  }
}

export function trackWebsiteSearch(query: string, sid: string) {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.SEARCH, { query, sid, fr: 'feed' });
}

export function trackWebsiteChat(badge: number | undefined) {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.CHAT, { badge, enter_place: 0 });
}

export function trackWebsiteNavigationShow(imBadge: number | undefined, companyBadge: number | undefined) {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.NAVIGATION_SHOW, {
    top_navi_name_and_badge: {
      im_badge_number: imBadge,
      company_badge_number: companyBadge,
    },
  });
}

export function trackWebsiteCompanyGuide(targetType: 'click_nav' | 'click_tip' | 'close' | 'show') {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.COMPANY_GUIDE, { target_type: targetType });
}

export function trackWebsiteReferralShow() {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.REFERRAL_SHOW);
}

export function trackWebsiteReferralClick() {
  track(PLATFORM_WEBSITE_TRACKING_EVENTS.REFERRAL_CLICK);
}
